import React, {
  MutableRefObject,
  Ref,
  useEffect,
  useRef,
} from 'react';
import clsx from 'clsx';
import {
  Draggable,
} from '@hello-pangea/dnd';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  InsuranceCardDragItem,
} from 'views/EMR/components/Insurance/insuranceCardDnD';

type Props = {
  item: InsuranceCardDragItem;
  index: number;
  isDragDisabled?: boolean;
  isPositionLocked?: boolean;
  className?: string;
  draggingClassName?: string;
  previewClassName?: string;
  draggingPreviewClassName?: string;
  containerRef?: Ref<HTMLDivElement>;
};

function setRef<TElement>(
  ref: Ref<TElement> | undefined,
  value: TElement | null,
) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as MutableRefObject<TElement | null>).current = value;
  }
}

export default function InsuranceCardMediaThumbnail({
  item,
  index,
  isDragDisabled = false,
  isPositionLocked = false,
  className,
  draggingClassName,
  previewClassName,
  draggingPreviewClassName,
  containerRef,
}: Props) {
  const containerNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setRef(containerRef, containerNodeRef.current);

    return () => {
      setRef(containerRef, null);
    };
  }, [containerRef]);

  return (
    <Draggable
      draggableId={item.dragId}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => {
        const cursorClassName = clsx({
          '!cursor-grab': !isDragDisabled && !snapshot.isDragging,
          '!cursor-grabbing': !isDragDisabled && snapshot.isDragging,
          '!cursor-not-allowed': isDragDisabled,
        });
        const draggableStyle = isPositionLocked && !snapshot.isDragging
          ? {
            ...provided.draggableProps.style,
            transform: 'none',
          }
          : provided.draggableProps.style;

        return (
          <div
            ref={(node) => {
              containerNodeRef.current = node;
              provided.innerRef(node);
            }}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={draggableStyle}
            className={clsx(
              'overflow-hidden rounded-md border border-gray-200 bg-gray-50 transition-shadow',
              cursorClassName,
              className,
              snapshot.isDragging && draggingClassName,
              {
                'hover:border-blue-200 hover:ring-1 hover:ring-blue-100 hover:shadow-sm':
                  !isDragDisabled,
                'opacity-60': isDragDisabled,
                'ring-2 ring-blue-300 shadow-sm': snapshot.isDragging,
              },
            )}
          >
            <MediaPreview
              media={item.previewMedia}
              thumbnail
              coverImage={false}
              className={clsx(
                'h-full w-full',
                cursorClassName,
                previewClassName,
                snapshot.isDragging && draggingPreviewClassName,
              )}
            />
          </div>
        );
      }}
    </Draggable>
  );
}
